import { useEffect, useMemo, useRef, useState, type JSX, type ReactNode } from 'react';
import { AlertTriangle, Building2, Construction, Eye, EyeOff, Loader2, RotateCcw, Smartphone, X } from 'lucide-react';
import type { ProjectPlan, ProjektVerortung } from '../../types';
import { gpsZustandAnzeige, useStandortSensoren } from '../../services/standortSensoren';
import {
    druckMedian, ebeneBestaetigen, ebeneSchaetzen, geruestLagen, geschossEbenen, ortsvorschlagBilden, schnappschussBilden,
    useVerortungGeraet, useVerortungsSitzung,
    type Ebene, type EbenenSchaetzung, type Ortsvorschlag, type SensorSchnappschuss, type VerortungsSitzung,
} from '../../services/verortung';
import { compressImage } from '../../services/imageService';
import { registriereZurueck } from '../../services/appUmfang';
import {
    blickText, canvasZielmasse, ebenenText, hoeheText, kameraFehlerText, NULLPUNKT_ALT_MIN, nullpunktAlterText,
    pfeilWinkel, stempelLayout, stempelText,
} from './kameraBild';

/**
 * Kamera mit Live-Einblendung der Sensor-Verortung (docs/verortung-kamera-vertrag.md,
 * Paket B; Plan docs/maengel-verortung-plan.md Abschnitt 9).
 *
 *   • Vollbild-Kamerabild (Rückkamera über getUserMedia), oben halbtransparent
 *     „vermutlich 2. OG“ bzw. „Gerüst 4. Lage“ mit Höhe, Sicherheit, GPS ±m,
 *     Kompass-Pfeil und Alter des Nullpunkts — 5/s aufgefrischt.
 *   • Unten die Ebenen als große Chips („Hier bin ich“): Tippen nullt die
 *     Begehung neu (Median-Druck jetzt) und meldet es dem Aufrufer.
 *   • Auslöser: Bild + Sensor-Schnappschuss + Ortsvorschlag im selben Moment,
 *     optional mit Fotostempel, durch dieselbe Pipeline wie der Wizard.
 *
 * Abschaltbar: Knopf „Einblendung aus“ (Gerät) schaltet Einblendung UND
 * Sensoren ab; „Systemkamera“ ist der Ausweg zur gewohnten Kamera-App.
 * Datenschutz: Sensoren laufen nur, solange die Kamera offen und die
 * Einblendung an ist; gespeichert wird hier nichts außer der Sitzung
 * (Nullpunkt, sessionStorage) — das Bild geht nur an den Aufrufer.
 */

export interface KameraAufnahme {
    bild: string;
    schnappschuss: SensorSchnappschuss;
    vorschlag: Ortsvorschlag;
}

export interface VerortungsKameraProps {
    /** Geschosse, Gerüst, Plan-Kalibrierung. */
    verortung: ProjektVerortung;
    plaene: readonly ProjectPlan[];
    /** Schlüssel der Begehungs-Sitzung (useVerortungsSitzung). */
    sitzungSchluessel: string;
    titel?: string;
    /** Fehlt → reine Vorschau (Messmodus), kein Auslöser. */
    onAufnahme?: (a: KameraAufnahme) => void;
    /** Chip getippt („Hier bin ich“): nach dem Neu-Nullen, mit Schätzung VOR der Bestätigung. */
    onBestaetigt?: (ebene: Ebene, schnappschuss: SensorSchnappschuss, vorher: EbenenSchaetzung | null) => void;
    /** Ausweg Systemkamera (Wizard öffnet dann <input capture>). Fehlt → Knopf ausgeblendet. */
    onSystemkamera?: () => void;
    onSchliessen: () => void;
    /** Noch erlaubte Fotos (Wizard: max 3) — Anzeige; 0 sperrt den Auslöser. */
    restFotos?: number;
}

type KameraZustand =
    | { art: 'startet' }
    | { art: 'laeuft' }
    | { art: 'angehalten' }
    | { art: 'fehler'; text: string };

interface Meldung {
    text: string;
    ton: 'info' | 'fehler';
}

/** Takt der Einblendung (5/s). */
const TAKT_MS = 200;
/** Median-Fenster des Luftdrucks beim Bestätigen — wie Messmodus und Schnappschuss. */
const MEDIAN_FENSTER_MS = 10_000;
/** Kurze Rückmeldungen („Bestätigt: 2. OG“) verschwinden nach dieser Zeit; Fehler bleiben. */
const MELDUNG_MS = 3_000;
/** Ab dieser GPS-Genauigkeit gibt es keinen Pin (Vertrag: maxGpsGenauigkeitM 8). */
const GPS_PIN_GRENZE_M = 8;
/** JPEG-Qualität des Vollbilds vor der Wizard-Pipeline. */
const JPEG_QUALITAET = 0.92;

const KAMERA_VORGABE: MediaStreamConstraints = {
    video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
    audio: false,
};

const NICHTS = (): void => undefined;

// ─── Zeichnen ───────────────────────────────────────────────────────────────

/** Stempelbalken unten ins Bild; die Schrift schrumpft, bis der Text passt (mind. 12 px). */
function stempelZeichnen(ctx: CanvasRenderingContext2D, breite: number, hoehe: number, text: string): void {
    const layout = stempelLayout(breite);
    const platz = breite - 2 * layout.randPx;
    let schrift = layout.schriftPx;
    const schriftSetzen = () => { ctx.font = `600 ${schrift}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`; };
    schriftSetzen();
    while (schrift > 12 && ctx.measureText(text).width > platz) {
        schrift -= 1;
        schriftSetzen();
    }
    const balken = Math.round(schrift * 1.9);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(0, hoehe - balken, breite, balken);
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, layout.randPx, hoehe - balken / 2, platz);
}

function canvasZuBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('Kamerabild konnte nicht umgewandelt werden'))),
            'image/jpeg',
            JPEG_QUALITAET,
        );
    });
}

const fehlerText = (e: unknown): string => (e instanceof Error && e.message ? e.message : String(e));

// ─── Bausteine ──────────────────────────────────────────────────────────────

/** Kompass: Kreis mit Nordmarke, Pfeil im Uhrzeigersinn um den Blickwinkel (wahr Nord) gedreht. */
function KompassPfeil({ grad }: { grad: number }): JSX.Element {
    return (
        <svg viewBox="0 0 40 40" className="h-10 w-10 shrink-0" aria-hidden="true">
            <circle cx="20" cy="20" r="18" fill="rgba(0,0,0,0.35)" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" />
            <path d="M20 1.5 L22.5 6 L17.5 6 Z" fill="#f87171" />
            <g transform={`rotate(${grad} 20 20)`}>
                <path d="M20 8 L27 29 L20 24.5 L13 29 Z" fill="#38bdf8" stroke="#ffffff" strokeWidth="1.2" strokeLinejoin="round" />
            </g>
        </svg>
    );
}

function Schaltknopf({ onClick, children, beschriftung }: { onClick: () => void; children: ReactNode; beschriftung: string }): JSX.Element {
    return (
        <button
            type="button"
            onClick={onClick}
            className="min-h-[3.25rem] min-w-[4.5rem] rounded-2xl bg-black/55 px-3 py-2 text-xs font-semibold text-white flex flex-col items-center justify-center gap-1 active:bg-black/80"
        >
            {children}
            <span>{beschriftung}</span>
        </button>
    );
}

// ─── Kamera ─────────────────────────────────────────────────────────────────

export function VerortungsKamera(p: VerortungsKameraProps): JSX.Element {
    const { verortung, plaene, sitzungSchluessel, titel, onAufnahme, onBestaetigt, onSystemkamera, onSchliessen, restFotos } = p;

    const [geraet, geraetSetzen] = useVerortungGeraet();
    // Gerät oder Projekt aus (Vertrag Grundsatz 1/2, Codex-Review 27.09. P1): auch hier keine
    // Einblendung und keine Sensoren — egal, welcher Aufrufer die Kamera öffnet.
    const einblendungAn = geraet.aktiv && geraet.einblendung && verortung.aktiv;
    const sensoren = useStandortSensoren(einblendungAn);
    const [sitzung, setSitzung] = useVerortungsSitzung(sitzungSchluessel);

    const [jetzt, setJetzt] = useState(0);
    const [kamera, setKamera] = useState<KameraZustand>({ art: 'startet' });
    const [laeuft, setLaeuft] = useState(false);
    const [meldung, setMeldung] = useState<Meldung | null>(null);

    const flaecheRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    /** Schutz gegen Doppeltipp: der State kommt erst mit dem nächsten Render. */
    const laeuftRef = useRef(false);
    /** false nach dem Aushängen — eine späte Aufnahme geht dann nicht mehr an den Aufrufer. */
    const offenRef = useRef(true);
    const meldungTimer = useRef<number | null>(null);
    const kameraSteuerung = useRef<{ fortsetzen: () => void; anhalten: () => void }>({ fortsetzen: NICHTS, anhalten: NICHTS });
    const schliessenRef = useRef<() => void>(onSchliessen);

    // Android-Zurück-Taste = Schließen (appUmfang.registriereZurueck; ein fremder Dialog darüber hat Vorrang).
    useEffect(() => registriereZurueck(() => schliessenRef.current(), () => flaecheRef.current), []);

    useEffect(() => {
        offenRef.current = true;
        return () => { offenRef.current = false; };
    }, []);

    useEffect(() => () => { if (meldungTimer.current !== null) window.clearTimeout(meldungTimer.current); }, []);

    // Uhr der Einblendung — nur, solange sie an ist; nie Date.now() im Render.
    useEffect(() => {
        if (!einblendungAn) return undefined;
        const tick = () => setJetzt(Date.now());
        const erster = window.setTimeout(tick, 0);
        const takt = window.setInterval(tick, TAKT_MS);
        return () => { window.clearTimeout(erster); window.clearInterval(takt); };
    }, [einblendungAn]);

    // Kamerastrom: beim Öffnen starten; beim Schließen, Aushängen und im Hintergrund alle Tracks stoppen.
    useEffect(() => {
        let strom: MediaStream | null = null;
        let beendet = false;
        /** Jede Anfrage bekommt eine Nummer; eine überholte (gestoppt, neu gestartet) wird verworfen. */
        let versuch = 0;

        const stoppen = () => {
            versuch += 1;
            strom?.getTracks().forEach((t) => t.stop());
            strom = null;
            const v = videoRef.current;
            if (v) v.srcObject = null;
        };

        const starten = () => {
            if (beendet || strom || document.visibilityState === 'hidden') return;
            versuch += 1;
            const nr = versuch;
            const medien = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
            const anfrage = medien && typeof medien.getUserMedia === 'function'
                ? medien.getUserMedia(KAMERA_VORGABE)
                : Promise.reject(Object.assign(new Error('Keine Kamera-Schnittstelle'), { name: 'NotSupportedError' }));
            anfrage.then((s) => {
                if (beendet || nr !== versuch) {
                    s.getTracks().forEach((t) => t.stop());
                    return;
                }
                strom = s;
                const v = videoRef.current;
                if (v) {
                    v.srcObject = s;
                    void v.play().catch(() => undefined);
                }
                setKamera({ art: 'laeuft' });
            }).catch((e: unknown) => {
                if (beendet || nr !== versuch) return;
                setKamera({ art: 'fehler', text: kameraFehlerText(e) });
            });
        };

        const fortsetzen = () => {
            if (beendet || strom) return;
            setKamera({ art: 'startet' });
            starten();
        };

        // Zurück aus den Android-Einstellungen oder der Systemkamera: neu starten (auch nach einem Fehler).
        const sichtbarkeit = () => {
            if (document.visibilityState === 'hidden') stoppen();
            else fortsetzen();
        };

        kameraSteuerung.current = {
            fortsetzen,
            anhalten: () => {
                if (beendet) return;
                stoppen();
                setKamera({ art: 'angehalten' });
            },
        };
        starten();
        document.addEventListener('visibilitychange', sichtbarkeit);
        return () => {
            beendet = true;
            document.removeEventListener('visibilitychange', sichtbarkeit);
            stoppen();
            kameraSteuerung.current = { fortsetzen: NICHTS, anhalten: NICHTS };
        };
    }, []);

    // ─── Ebenen und Schätzung ───────────────────────────────────────────────

    const geschossListe = useMemo(() => geschossEbenen(verortung.geschosse), [verortung.geschosse]);
    const lagenListe = useMemo(() => geruestLagen(verortung.geruest), [verortung.geruest]);
    const hatGeschosse = geschossListe.length > 0;
    const hatGeruest = lagenListe.length > 0;
    const eingerichtet = hatGeschosse || hatGeruest;
    // Nur ein Gerüst eingerichtet → Gerüst-Modus; Gerüst-Modus ohne Gerüst (Projekt geändert) → Geschosse.
    const geruestModus = hatGeruest && (sitzung.geruestModus || !hatGeschosse);
    const sitzungWirksam = useMemo<VerortungsSitzung>(
        () => (sitzung.geruestModus === geruestModus ? sitzung : { ...sitzung, geruestModus }),
        [sitzung, geruestModus],
    );
    // Wie im Aufzug: oben zuerst.
    const chips = useMemo(
        () => [...(geruestModus ? lagenListe : geschossListe)].sort((a, b) => b.hoeheM - a.hoeheM),
        [geruestModus, lagenListe, geschossListe],
    );

    const proben = sensoren.druckProben;
    // Die Uhr tickt im Takt: Proben, die jünger sind als der letzte Tick, trotzdem mitnehmen (wie Messmodus).
    const zeit = Math.max(jetzt, proben.length ? proben[proben.length - 1].t : 0);
    const schnappschuss = useMemo(
        () => (einblendungAn
            ? schnappschussBilden(sensoren.druckProben, sensoren.positionen, sensoren.richtung, zeit)
            : schnappschussBilden([], [], null, zeit)),
        [einblendungAn, sensoren.druckProben, sensoren.positionen, sensoren.richtung, zeit],
    );
    const vorschlag = useMemo(
        () => ortsvorschlagBilden({ verortung, plaene, sitzung: sitzungWirksam, schnappschuss }),
        [verortung, plaene, sitzungWirksam, schnappschuss],
    );
    const schaetzung = vorschlag.schaetzung;

    const keinBarometer = sensoren.pruefung?.barometer === false;
    const chipsGesperrt = !sensoren.verfuegbar || keinBarometer;
    const fotosGesperrt = typeof restFotos === 'number' && restFotos <= 0;
    const ausloeserBereit = kamera.art === 'laeuft' && !laeuft && !fotosGesperrt;

    // ─── Handler ────────────────────────────────────────────────────────────

    const melden = (text: string, ton: Meldung['ton']) => {
        if (meldungTimer.current !== null) window.clearTimeout(meldungTimer.current);
        meldungTimer.current = null;
        setMeldung({ text, ton });
        if (ton === 'info') {
            meldungTimer.current = window.setTimeout(() => {
                meldungTimer.current = null;
                setMeldung(null);
            }, MELDUNG_MS);
        }
    };

    const ebeneTippen = (ebene: Ebene) => {
        if (!einblendungAn || chipsGesperrt) return;
        const t = Date.now();
        const fenster = Math.max(t, proben.length ? proben[proben.length - 1].t : 0);
        const median = druckMedian(proben, fenster, MEDIAN_FENSTER_MS);
        if (!median) {
            melden('Noch kein Luftdruck — ein paar Sekunden still halten, dann erneut antippen.', 'fehler');
            return;
        }
        const schnapp = schnappschussBilden(proben, sensoren.positionen, sensoren.richtung, fenster);
        const vorher = ebeneSchaetzen(verortung.geschosse, verortung.geruest, sitzungWirksam, median.hPa, t);
        setSitzung(ebeneBestaetigen(sitzung, ebene, median.hPa, t));
        onBestaetigt?.(ebene, schnapp, vorher);
        melden(`Bestätigt: ${ebene.name}`, 'info');
    };

    const modusWechseln = () => setSitzung({ ...sitzung, geruestModus: !geruestModus });

    const ausloesen = async () => {
        if (!onAufnahme || laeuftRef.current || fotosGesperrt) return;
        const video = videoRef.current;
        if (!video || kamera.art !== 'laeuft') return;
        const masse = canvasZielmasse(video.videoWidth, video.videoHeight);
        if (!masse) {
            melden('Kamerabild noch nicht bereit — kurz warten.', 'fehler');
            return;
        }
        laeuftRef.current = true;
        setLaeuft(true);
        const canvas = document.createElement('canvas');
        try {
            // Schnappschuss im Moment der Aufnahme; Einblendung aus ⇒ leer (keine Sensoren).
            const t = Date.now();
            const schnapp = einblendungAn
                ? schnappschussBilden(sensoren.druckProben, sensoren.positionen, sensoren.richtung, t)
                : schnappschussBilden([], [], null, t);
            const vorschlagJetzt = ortsvorschlagBilden({ verortung, plaene, sitzung: sitzungWirksam, schnappschuss: schnapp });
            canvas.width = masse.breite;
            canvas.height = masse.hoehe;
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('kein Zeichenbereich');
            const q = masse.quelle;
            ctx.drawImage(video, q.x, q.y, q.breite, q.hoehe, 0, 0, masse.breite, masse.hoehe);
            if (geraet.fotostempel) {
                const s = vorschlagJetzt.schaetzung;
                stempelZeichnen(ctx, masse.breite, masse.hoehe, stempelText({
                    ebene: ebenenText(s),
                    hoeheM: s && Number.isFinite(s.hoeheM) ? s.hoeheM : null,
                    t,
                }));
            }
            const blob = await canvasZuBlob(canvas);
            const datei = new File([blob], `foto-${t}.jpg`, { type: 'image/jpeg', lastModified: t });
            const bild = await compressImage(datei, 800, 0.55);
            if (!offenRef.current) return;
            onAufnahme({ bild, schnappschuss: schnapp, vorschlag: vorschlagJetzt });
            melden('Foto übernommen', 'info');
        } catch (e) {
            if (offenRef.current) melden(`Foto fehlgeschlagen: ${fehlerText(e)}`, 'fehler');
        } finally {
            // Vollbild-Canvas sofort freigeben (bis zu 4096 px Kante).
            canvas.width = 0;
            canvas.height = 0;
            laeuftRef.current = false;
            if (offenRef.current) setLaeuft(false);
        }
    };

    // Kamera sofort freigeben, nicht erst beim Aushängen (Vertrag: bei Schließen stoppen).
    const schliessen = () => {
        kameraSteuerung.current.anhalten();
        onSchliessen();
    };

    const systemkamera = () => {
        // Kamera freigeben, bevor die Kamera-App sie braucht.
        kameraSteuerung.current.anhalten();
        onSystemkamera?.();
    };

    // Die Zurück-Taste (angemeldet beim Öffnen) nimmt immer den aktuellen Schließen-Weg.
    useEffect(() => { schliessenRef.current = schliessen; });

    // ─── Einblendung: Texte ─────────────────────────────────────────────────

    let hauptzeile: string;
    let hauptHinweis = true;
    if (!eingerichtet) hauptzeile = 'Verortung nicht eingerichtet';
    else if (!sensoren.verfuegbar) hauptzeile = 'Sensoren nicht verfügbar — App aktualisieren';
    else if (keinBarometer) hauptzeile = 'Kein Luftdrucksensor — Geschoss nicht erkennbar';
    else if (!sitzung.nullpunkt) hauptzeile = geruestModus ? 'Wo sind Sie? Lage antippen' : 'Wo sind Sie? Geschoss antippen';
    else if (schnappschuss.druckHpa === null) hauptzeile = 'Luftdruck wird gelesen …';
    else {
        hauptHinweis = false;
        const ebene = ebenenText(schaetzung);
        hauptzeile = !ebene ? '—' : schaetzung?.zwischen ? ebene : `vermutlich ${ebene}`;
    }
    const mitSchaetzung = !hauptHinweis && schaetzung !== null && Number.isFinite(schaetzung.hoeheM);
    const zweiteZeile = mitSchaetzung && schaetzung
        ? `${hoeheText(schaetzung.hoeheM)}${schaetzung.zwischen ? '' : ` · Sicherheit ${Math.round(schaetzung.sicherheit * 100)} %`}`
        : null;
    const alternative = mitSchaetzung && schaetzung?.alternative ? `oder ${schaetzung.alternative.name}` : null;

    const gpsZustand = gpsZustandAnzeige(sensoren.status, sensoren.pruefung);
    const pos = schnappschuss.position;
    const gps: { text: string; gut: boolean } | null = pos
        ? { text: `GPS ±${Math.max(1, Math.round(pos.genauigkeitM))} m`, gut: pos.genauigkeitM <= GPS_PIN_GRENZE_M }
        : gpsZustand === 'aus' ? { text: 'GPS aus', gut: false }
            : gpsZustand === 'keine_erlaubnis' ? { text: 'GPS nicht erlaubt', gut: false }
                : gpsZustand === 'nicht_vorhanden' || !sensoren.verfuegbar ? null
                    : { text: 'GPS sucht …', gut: false };

    const blick = pfeilWinkel(vorschlag.blickGradWahr);
    const kompassUngenau = blick === null && schnappschuss.richtung !== null && schnappschuss.richtung.genauigkeit < 2;

    const alterMin = !sitzung.nullpunkt ? null
        : schaetzung ? schaetzung.nullpunktAlterMin
            : jetzt > 0 ? Math.max(0, (jetzt - sitzung.nullpunkt.t) / 60_000) : null;
    const nullpunktAlt = alterMin !== null && alterMin >= NULLPUNKT_ALT_MIN;
    const aktivId = !hauptHinweis ? schaetzung?.ebene?.id ?? null : null;

    // ─── Oberfläche ─────────────────────────────────────────────────────────

    return (
        <div
            ref={flaecheRef}
            role="dialog"
            aria-modal="true"
            aria-label={titel ?? 'Kamera'}
            className="fixed inset-0 z-[130] bg-black text-white flex flex-col overflow-hidden select-none"
        >
            <video ref={videoRef} playsInline muted autoPlay className="absolute inset-0 h-full w-full object-cover" />

            {/* ─── Kopf + Einblendung ─────────────────────────────────────── */}
            <div
                className="relative z-10 bg-gradient-to-b from-black/75 via-black/45 to-transparent px-3 pb-8"
                style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
            >
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={schliessen}
                        className="min-h-[3rem] rounded-full bg-black/55 pl-3 pr-4 text-sm font-semibold flex items-center gap-1.5 active:bg-black/80"
                    >
                        <X className="h-6 w-6" /> Schließen
                    </button>
                    <p className="flex-1 min-w-0 truncate text-sm font-semibold text-white/90">{titel ?? ''}</p>
                    {typeof restFotos === 'number' && (
                        <span className="shrink-0 rounded-full bg-black/55 px-3 py-1.5 text-xs font-semibold">
                            {restFotos === 1 ? 'noch 1 Foto' : `noch ${Math.max(0, restFotos)} Fotos`}
                        </span>
                    )}
                </div>

                {einblendungAn && (
                    <div className="mt-3 rounded-2xl bg-black/45 px-4 py-3 backdrop-blur-[2px]">
                        <p className={`font-bold leading-tight ${hauptHinweis ? 'text-xl text-amber-200' : 'text-3xl'}`}>{hauptzeile}</p>
                        {zweiteZeile && <p className="mt-1 text-lg font-semibold text-white/95">{zweiteZeile}</p>}
                        {alternative && <p className="text-base font-semibold text-sky-200">{alternative}</p>}
                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm font-semibold">
                            {gps && <span className={gps.gut ? 'text-emerald-300' : 'text-white/80'}>{gps.text}</span>}
                            {blick !== null && (
                                <span className="flex items-center gap-1.5">
                                    <KompassPfeil grad={blick} /> {blickText(blick)}
                                </span>
                            )}
                            {kompassUngenau && <span className="text-amber-200">Kompass ungenau</span>}
                        </div>
                        {alterMin !== null && (
                            <p className={`mt-1 text-xs ${nullpunktAlt ? 'text-amber-200' : 'text-white/60'}`}>
                                {nullpunktAlterText(alterMin)}
                                {nullpunktAlt && ' — Ebene neu antippen'}
                            </p>
                        )}
                        {sensoren.fehler && (
                            <p className="mt-1 text-xs text-amber-200 flex items-start gap-1">
                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {sensoren.fehler}
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* ─── Mitte: Zustand der Kamera ──────────────────────────────── */}
            <div className="relative z-10 flex-1 flex items-center justify-center px-6">
                {kamera.art === 'startet' && (
                    <p className="rounded-full bg-black/55 px-4 py-2 text-sm font-semibold flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" /> Kamera startet …
                    </p>
                )}
                {kamera.art === 'angehalten' && (
                    <button
                        type="button"
                        onClick={() => kameraSteuerung.current.fortsetzen()}
                        className="min-h-[3.25rem] rounded-2xl bg-white/90 px-5 text-base font-semibold text-slate-900 flex items-center gap-2 active:bg-white"
                    >
                        <RotateCcw className="h-5 w-5" /> Kamera fortsetzen
                    </button>
                )}
                {kamera.art === 'fehler' && (
                    <div className="max-w-sm rounded-2xl bg-slate-900/90 p-4 text-center" role="alert">
                        <AlertTriangle className="mx-auto h-8 w-8 text-amber-300" />
                        <p className="mt-2 text-base font-semibold">{kamera.text}</p>
                        <div className="mt-4 flex flex-wrap justify-center gap-2">
                            {onSystemkamera && (
                                <button
                                    type="button"
                                    onClick={systemkamera}
                                    className="min-h-[3rem] rounded-xl bg-sky-600 px-4 text-sm font-semibold flex items-center gap-2 active:bg-sky-700"
                                >
                                    <Smartphone className="h-5 w-5" /> Systemkamera
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => kameraSteuerung.current.fortsetzen()}
                                className="min-h-[3rem] rounded-xl border border-white/60 px-4 text-sm font-semibold flex items-center gap-2 active:bg-white/10"
                            >
                                <RotateCcw className="h-5 w-5" /> Erneut versuchen
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ─── Fuß: Ebenen-Chips, Auslöser, Schalter ──────────────────── */}
            <div className="relative z-10 bg-gradient-to-t from-black/85 via-black/55 to-transparent pt-8 pb-safe space-y-3">
                {meldung && (
                    <p
                        role="status"
                        onClick={() => setMeldung(null)}
                        className={`mx-4 rounded-xl px-3 py-2 text-sm font-semibold ${meldung.ton === 'fehler' ? 'bg-rose-600/90' : 'bg-slate-800/90'}`}
                    >
                        {meldung.text}
                    </p>
                )}

                {einblendungAn && eingerichtet && (
                    <div>
                        <p className="px-4 pb-1 text-xs font-semibold text-white/70">
                            {keinBarometer
                                ? 'Kein Luftdrucksensor — Geschoss nicht erkennbar'
                                : `Hier bin ich — ${geruestModus ? 'Lage' : 'Geschoss'} antippen`}
                        </p>
                        <div className="flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none]">
                            {hatGeruest && hatGeschosse && (
                                <button
                                    type="button"
                                    onClick={modusWechseln}
                                    className="shrink-0 min-h-[3.25rem] rounded-xl border-2 border-white/70 bg-black/45 px-4 text-base font-bold flex items-center gap-2 active:bg-black/70"
                                >
                                    {geruestModus
                                        ? <><Building2 className="h-5 w-5" /> Geschosse</>
                                        : <><Construction className="h-5 w-5" /> Gerüst</>}
                                </button>
                            )}
                            {chips.map((e) => (
                                <button
                                    key={e.id}
                                    type="button"
                                    disabled={chipsGesperrt}
                                    aria-pressed={aktivId === e.id}
                                    onClick={() => ebeneTippen(e)}
                                    className={`shrink-0 min-h-[3.25rem] min-w-[4.5rem] rounded-xl px-4 text-base font-bold transition-colors disabled:opacity-40 ${aktivId === e.id ? 'bg-sky-500 text-white ring-2 ring-white' : 'bg-white/90 text-slate-900 active:bg-white'}`}
                                >
                                    {e.art === 'geruest' ? e.name.replace(/^Gerüst\s+/, '') : e.name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {fotosGesperrt && onAufnahme && (
                    <p className="mx-4 text-center text-sm font-semibold text-amber-200">Höchstzahl der Fotos erreicht.</p>
                )}

                <div className="grid grid-cols-3 items-center px-4">
                    <div className="justify-self-start">
                        {verortung.aktiv && (
                            <Schaltknopf
                                onClick={() => geraetSetzen({ einblendung: !geraet.einblendung })}
                                beschriftung={geraet.einblendung ? 'Einblendung aus' : 'Einblendung an'}
                            >
                                {geraet.einblendung ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                            </Schaltknopf>
                        )}
                    </div>
                    <div className="justify-self-center">
                        {onAufnahme && (
                            <button
                                type="button"
                                onClick={() => { void ausloesen(); }}
                                disabled={!ausloeserBereit}
                                aria-label="Foto aufnehmen"
                                className="h-20 w-20 rounded-full border-4 border-white bg-white/20 flex items-center justify-center active:bg-white/50 disabled:opacity-40"
                            >
                                {laeuft
                                    ? <Loader2 className="h-8 w-8 animate-spin" />
                                    : <span className="block h-14 w-14 rounded-full bg-white" />}
                            </button>
                        )}
                    </div>
                    <div className="justify-self-end">
                        {onSystemkamera && (
                            <Schaltknopf onClick={systemkamera} beschriftung="Systemkamera">
                                <Smartphone className="h-5 w-5" />
                            </Schaltknopf>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
